/**
 * EHR Demo — Mock Epic-like EHR UI
 *
 * Demonstrates MedArrive integrations from the Virtual CCN Nurse's perspective:
 *   - Patient List (My Unit): MedArrive-fed Next Visit / Visit Status columns + pulsing join indicator
 *   - Schedule tab: schedule grid with SMART App status column + right-side vertical schedule panel
 *   - SMART Application: launches the MedArrive Predict or Coordinate page inline,
 *     replacing the EHR body beneath the tab strip.
 *
 * Variant ('predict' | 'coordinate') controls the SMART App nav label and which
 * MedArrive page is embedded.
 *
 * Routes: /ehr-demo/predict, /ehr-demo/coordinate — linked only from the Demo
 * Scenarios dialog. Not in sidebar nav.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Chip,
  Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SearchIcon from '@mui/icons-material/Search';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { format } from 'date-fns';

export type EHRDemoVariant = 'predict' | 'coordinate';

// ─── Design tokens (Epic-like palette) ───────────────────────────────────────
const C = {
  navBg:      '#1e1e20',
  homeBtn:    '#b07020',
  navHover:   '#3a3a3d',
  navActive:  '#3a3a3d',
  sidebar:    '#fafafa',
  content:    '#eff1f3',
  tableHead:  '#e4e6e9',
  border:     '#d0d2d5',
  blue:       '#005cb9',
  text:       '#1a1a1a',
  muted:      '#666',
  ma:         '#6b21a8', // MedArrive brand purple
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface EHRPatient {
  id: number; lastName: string; firstName: string; age: number; gender: string;
  room: string; mrn: string; provider: string; diagnosis: string;
  nextVisitTime: string | null;
  visitStatus: 'scheduled' | 'in-progress' | 'completed' | null;
  needsJoin: boolean;
}

interface ScheduleVisit {
  id: number; time: string; patientName: string; visitType: string;
  status: 'completed' | 'in-progress' | 'scheduled';
  startHour: number; // hours offset from 8 AM
  durationHours: number;
}

// ─── Mock data ────────────────────────────────────────────────────────────────
const TODAY_LABEL = format(new Date(), 'EEEE, MMMM d, yyyy');

const PATIENTS: EHRPatient[] = [
  { id: 1, lastName: 'Anderson',  firstName: 'Linda',  age: 77, gender: 'F', room: '412A', mrn: '518-744-4413', provider: 'Dr. Martinez', diagnosis: 'COPD Exacerbation',          nextVisitTime: '10:30 AM', visitStatus: 'in-progress', needsJoin: true  },
  { id: 2, lastName: 'Martinez',  firstName: 'Rosa',   age: 68, gender: 'F', room: '308B', mrn: '294-501-8834', provider: 'Dr. Kim',      diagnosis: 'CHF Exacerbation',           nextVisitTime: '11:15 AM', visitStatus: 'scheduled',   needsJoin: false },
  { id: 3, lastName: 'Thompson',  firstName: 'David',  age: 71, gender: 'M', room: '215C', mrn: '773-209-5512', provider: 'Dr. Patel',    diagnosis: 'Post-Surgical Wound Care',   nextVisitTime: '1:00 PM',  visitStatus: 'scheduled',   needsJoin: false },
  { id: 4, lastName: 'Patel',     firstName: 'Anita',  age: 59, gender: 'F', room: '521A', mrn: '441-882-3301', provider: 'Dr. Chen',     diagnosis: 'Cellulitis — IV Antibiotics',nextVisitTime: '2:45 PM',  visitStatus: 'scheduled',   needsJoin: false },
  { id: 5, lastName: 'Williams',  firstName: 'James',  age: 82, gender: 'M', room: '118D', mrn: '629-330-7744', provider: 'Dr. Rodriguez',diagnosis: 'Pneumonia',                  nextVisitTime: null,       visitStatus: 'completed',   needsJoin: false },
];

const SCHEDULE: ScheduleVisit[] = [
  { id: 1, time: '9:00 AM',  patientName: 'James Williams',  visitType: 'Telehealth Rounding',  status: 'completed',   startHour: 1,    durationHours: 0.75 },
  { id: 2, time: '10:30 AM', patientName: 'Linda Anderson',  visitType: 'Telehealth Rounding',  status: 'in-progress', startHour: 2.5,  durationHours: 1    },
  { id: 3, time: '11:15 AM', patientName: 'Rosa Martinez',   visitType: 'Telehealth Rounding',  status: 'scheduled',   startHour: 3.25, durationHours: 1    },
  { id: 4, time: '1:00 PM',  patientName: 'David Thompson',  visitType: 'Telehealth Rounding',  status: 'scheduled',   startHour: 5,    durationHours: 1    },
  { id: 5, time: '2:45 PM',  patientName: 'Anita Patel',     visitType: 'Telehealth Check-in',  status: 'scheduled',   startHour: 6.75, durationHours: 0.75 },
];

const PATIENT_AGE: Record<string, number> = Object.fromEntries(
  PATIENTS.map(p => [`${p.firstName} ${p.lastName}`, p.age])
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const HOUR_HEIGHT = 76; // px per hour in vertical timeline
const SCHED_START = 8;  // 8 AM
const SCHED_HOURS = 11; // 8 AM → 7 PM

function hourLabel(h: number) {
  const disp = h % 12 || 12;
  return `${disp}${h < 12 ? 'am' : 'pm'}`;
}
function visitBg(s: string)     { return s === 'completed' ? '#dcfce7' : s === 'in-progress' ? '#dbeafe' : '#bfdbfe'; }
function visitBorder(s: string) { return s === 'completed' ? '#16a34a' : s === 'in-progress' ? '#2563eb' : '#60a5fa'; }

// ─── Vertical Schedule Panel (right side on Schedule tab) ─────────────────────
function VerticalSchedulePanel() {
  return (
    <Box sx={{ width: 278, borderLeft: `1px solid ${C.border}`, bgcolor: '#fff', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${C.border}`, bgcolor: C.tableHead, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: C.ma, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
          MedArrive Schedule
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: C.muted }}>Sarah Johnson · Today</Typography>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box sx={{ position: 'relative', height: SCHED_HOURS * HOUR_HEIGHT }}>
          {/* Hour lines */}
          {Array.from({ length: SCHED_HOURS }, (_, i) => (
            <Box key={i} sx={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, height: HOUR_HEIGHT, borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ position: 'absolute', top: 5, left: 7, fontSize: '0.63rem', color: C.muted, lineHeight: 1 }}>
                {hourLabel(SCHED_START + i)}
              </Typography>
            </Box>
          ))}
          {/* Visit cards */}
          {SCHEDULE.map(v => (
            <Box
              key={v.id}
              sx={{
                position: 'absolute',
                top: v.startHour * HOUR_HEIGHT + 2,
                left: 38,
                right: 6,
                height: Math.max(v.durationHours * HOUR_HEIGHT - 4, 30),
                bgcolor: visitBg(v.status),
                borderLeft: `3px solid ${visitBorder(v.status)}`,
                borderRadius: '0 4px 4px 0',
                px: 1,
                py: 0.5,
                overflow: 'hidden',
                ...(v.status === 'in-progress' ? {
                  '@keyframes vPulse': {
                    '0%, 100%': { boxShadow: `0 0 0 0 ${visitBorder('in-progress')}55` },
                    '60%':       { boxShadow: `0 0 0 6px transparent` },
                  },
                  animation: 'vPulse 2s ease-in-out infinite',
                } : {}),
              }}
            >
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#1e3a5f', lineHeight: 1.3 }}>{v.time}</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#334155', lineHeight: 1.2 }}>{v.visitType} · Virtual CCN</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#1e3a5f', lineHeight: 1.3 }}>{v.patientName}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── My Unit Patient List ─────────────────────────────────────────────────────
function MyUnitView({ onSelect }: { onSelect: (p: EHRPatient) => void }) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Patient', 'Room/Bed', 'MRN', 'Procedures', 'Provider'].map(h => (
              <TableCell key={h} sx={{ bgcolor: C.tableHead, fontWeight: 700, fontSize: '0.75rem', borderBottom: `2px solid ${C.border}` }}>{h}</TableCell>
            ))}
            {/* MedArrive-fed columns */}
            {['Next Visit', 'Visit Status', ''].map(h => (
              <TableCell key={h} sx={{ bgcolor: C.tableHead, fontWeight: 700, fontSize: '0.75rem', borderBottom: `2px solid ${C.border}` }}>
                {h}
                {h !== '' && <Typography sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 400, color: C.ma }}>via MedArrive</Typography>}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {PATIENTS.map((p, i) => (
            <TableRow key={p.id} hover onClick={() => onSelect(p)} sx={{ cursor: 'pointer', bgcolor: i % 2 ? '#f8f9fa' : '#fff', '&:hover': { bgcolor: '#e8f0fe' } }}>
              <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                {p.lastName}, {p.firstName}
                <Typography component="span" sx={{ fontSize: '0.7rem', color: C.muted, ml: 0.5 }}>({p.age} y.o./{p.gender})</Typography>
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{p.room}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: C.muted }}>{p.mrn}</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: C.muted }}>—</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{p.provider}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', fontWeight: p.visitStatus === 'in-progress' ? 700 : 400 }}>
                {p.nextVisitTime ?? '—'}
              </TableCell>
              <TableCell>
                {p.visitStatus === 'completed'   && <Chip label="Completed"   size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.7rem' }} />}
                {p.visitStatus === 'scheduled'   && <Chip label="Scheduled"   size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.7rem' }} />}
                {p.visitStatus === 'in-progress' && <Chip label="In Progress" size="small" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.7rem' }} />}
              </TableCell>
              <TableCell sx={{ width: 44, textAlign: 'center', pr: 1 }}>
                {p.needsJoin && (
                  <Tooltip title="Join Telehealth Visit">
                    <VideoCallIcon sx={{
                      color: C.blue, fontSize: 26,
                      '@keyframes joinPulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%':       { opacity: 0.25, transform: 'scale(1.35)' },
                      },
                      animation: 'joinPulse 1.2s ease-in-out infinite',
                      cursor: 'pointer',
                    }} />
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Schedule Grid View ───────────────────────────────────────────────────────
function ScheduleGridView() {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: C.text, mb: 1.5 }}>Schedule</Typography>
      <Box sx={{ display: 'flex', gap: 0, mb: 2, flexWrap: 'wrap' }}>
        {['+ Add Patient','Refresh','Schedule Visit','Check In','Registration','Cancel Visit','Visit Note','Orders','Sign Out','+ Addtl Tools'].map((a, i, arr) => (
          <Typography key={a} sx={{ fontSize: '0.75rem', color: C.blue, cursor: 'pointer', pr: 1, mr: 1, borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none', '&:hover': { textDecoration: 'underline' } }}>{a}</Typography>
        ))}
      </Box>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Time','Provider','Department','Patient','Age','Visit Type','Status','Appt. Type'].map(h => (
              <TableCell key={h} sx={{ bgcolor: C.tableHead, fontWeight: 700, fontSize: '0.75rem', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</TableCell>
            ))}
            <TableCell sx={{ bgcolor: C.tableHead, fontWeight: 700, fontSize: '0.75rem', borderBottom: `2px solid ${C.border}` }}>
              SMART App
              <Typography sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 400, color: C.ma }}>via MedArrive</Typography>
            </TableCell>
            <TableCell sx={{ bgcolor: C.tableHead, fontWeight: 700, fontSize: '0.75rem', borderBottom: `2px solid ${C.border}` }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {SCHEDULE.map((v, i) => (
            <TableRow key={v.id} hover sx={{ cursor: 'pointer', bgcolor: i % 2 ? '#f8f9fa' : '#fff', '&:hover': { bgcolor: '#e8f0fe' } }}>
              <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{v.time}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Dr. Johnson</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: C.muted }}>CCN Services</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{v.patientName}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{PATIENT_AGE[v.patientName] ?? '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>{v.visitType}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem' }}>
                {v.status === 'completed'   ? 'Checked Out' :
                 v.status === 'in-progress' ? 'Checked In'  : 'Scheduled'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: C.muted }}>Virtual</TableCell>
              <TableCell>
                {v.status === 'in-progress' && (
                  <Chip label="LIVE" size="small" sx={{
                    bgcolor: '#dc2626', color: '#fff', fontWeight: 700, fontSize: '0.65rem', height: 20,
                    '@keyframes liveBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                    animation: 'liveBlink 1.2s ease-in-out infinite',
                  }} />
                )}
                {v.status === 'scheduled'  && <Chip label="Upcoming" size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />}
                {v.status === 'completed'  && <Chip label="Done"     size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: C.muted }}>Telehealth appointment</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Patient Summary Bottom Panel ────────────────────────────────────────────
function PatientSummaryPanel({ patient }: { patient: EHRPatient }) {
  const sections = [
    { label: 'Vitals',          body: 'BP 128/76\nHR 82\nTemp 98.4°F\nSaO₂ 96%' },
    { label: 'Allergies',       body: 'NKDA\nReviewed today' },
    { label: 'Active Problems', body: `${patient.diagnosis}\nHypertension` },
    { label: 'Medications',     body: 'Lisinopril 10 mg PO daily\nAlbuterol PRN' },
    { label: 'Orders',          body: '• CBC, CMP — collected\n• Chest X-ray — pending' },
    { label: 'Recent Results',  body: 'CBC: WBC 7.8, Hgb 14.1\nCMP: Na 138, K 4.2' },
  ];

  return (
    <Box sx={{ height: 192, borderTop: `2px solid ${C.border}`, bgcolor: '#fff', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
      {/* Tabs */}
      <Box sx={{ display: 'flex', borderBottom: `1px solid ${C.border}`, bgcolor: C.tableHead, flexShrink: 0 }}>
        {['Chart Review','Patient Summary','Plan','Physical Diagram','Wrap Up'].map((t, i) => (
          <Box key={t} sx={{ px: 2, py: 0.75, fontSize: '0.75rem', fontWeight: i === 1 ? 700 : 400, color: i === 1 ? C.blue : C.muted, borderBottom: i === 1 ? `2px solid ${C.blue}` : '2px solid transparent', cursor: 'pointer' }}>{t}</Box>
        ))}
      </Box>
      {/* Grid */}
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', overflow: 'hidden' }}>
        {sections.map(({ label, body }, i) => (
          <Box key={label} sx={{ p: 1.5, borderRight: i < 5 ? `1px solid ${C.border}` : 'none', overflow: 'hidden' }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: C.blue, mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: C.text, whiteSpace: 'pre-line', lineHeight: 1.45 }}>{body}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Main EHR Demo Component ──────────────────────────────────────────────────
export default function EHRDemo({ variant = 'predict' }: { variant?: EHRDemoVariant }) {
  const navigate = useNavigate();
  const [activeTab,  setActiveTab]  = useState<'patient-list' | 'schedule'>('patient-list');
  const [activeList, setActiveList] = useState<'my-patients' | 'my-unit'>('my-unit');
  const [selected,   setSelected]   = useState<EHRPatient>(PATIENTS[0]);
  const [appOpen,    setAppOpen]    = useState(false);

  // The SMART Application nav item is labeled and targeted per variant. Clicking
  // it embeds the corresponding MedArrive page inline (replacing the EHR body).
  // The "Coordinate" product lives under the /discharge route tree (labeled
  // "Coordinate" in the app nav).
  const smartLabel = variant === 'coordinate' ? 'Coordinate' : 'Predict';
  const smartSrc   = variant === 'coordinate' ? '/discharge' : '/predict';

  // Switching EHR tabs exits the embedded SMART app view.
  const selectTab = (key: 'patient-list' | 'schedule') => {
    setActiveTab(key);
    setAppOpen(false);
  };

  const navItems = [
    { label: 'Patient Lookup' },
    { label: 'Encounter' },
    { label: smartLabel, action: () => setAppOpen(o => !o), active: appOpen },
    { label: 'Media Manager' },
  ];

  return (
    <Box sx={{ height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0, display: 'flex', flexDirection: 'column', bgcolor: C.content, fontFamily: '"Segoe UI", Arial, sans-serif', overflow: 'hidden' }}>

      {/* ── Top Nav ────────────────────────────────────────────────────────── */}
      <Box sx={{ height: 48, bgcolor: C.navBg, display: 'flex', alignItems: 'center', flexShrink: 0, px: 1, gap: 0.5 }}>
        {/* Home Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: C.homeBtn, px: 1.5, py: 0.75, borderRadius: 0.5, gap: 1, cursor: 'pointer', mr: 1 }}>
          <Box sx={{ width: 22, height: 22, bgcolor: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: C.homeBtn }}>F</Typography>
          </Box>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Home Menu</Typography>
        </Box>
        {navItems.map(item => (
          <Box key={item.label} onClick={item.action} sx={{ px: 1.5, py: 0.75, cursor: item.action ? 'pointer' : 'default', borderRadius: 0.5, bgcolor: item.active ? C.navActive : 'transparent', border: item.active ? '1px solid #555' : '1px solid transparent', '&:hover': item.action ? { bgcolor: C.navHover } : {} }}>
            <Typography sx={{ color: item.active ? '#fff' : '#ccc', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{item.label}</Typography>
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        {/* Search bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#2d2d30', borderRadius: 0.5, px: 1.5, py: 0.5, gap: 1, minWidth: 190, mr: 1 }}>
          <SearchIcon sx={{ color: '#888', fontSize: 15 }} />
          <Typography sx={{ color: '#777', fontSize: '0.75rem' }}>Search patients…</Typography>
        </Box>
        <NotificationsNoneIcon sx={{ color: '#aaa', fontSize: 20, cursor: 'pointer', mr: 1 }} />
        <Typography sx={{ color: '#ccc', fontSize: '0.75rem', mr: 1 }}>sjohnson@health.org</Typography>
        <Avatar sx={{ width: 28, height: 28, bgcolor: C.blue, fontSize: '0.7rem', mr: 1 }}>SJ</Avatar>
        {/* ← Back to MedArrive */}
        <Box onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, cursor: 'pointer', borderRadius: 0.5, border: '1px solid #555', '&:hover': { bgcolor: C.navHover } }}>
          <ArrowBackIcon sx={{ color: '#a78bfa', fontSize: 14 }} />
          <Typography sx={{ color: '#a78bfa', fontSize: '0.75rem', fontWeight: 600 }}>MedArrive</Typography>
        </Box>
      </Box>

      {/* ── Tab strip ──────────────────────────────────────────────────────── */}
      <Box sx={{ height: 36, bgcolor: '#2a2a2d', display: 'flex', alignItems: 'flex-end', px: 2, flexShrink: 0 }}>
        {[
          { label: 'ED Trackboard', key: 'ed' },
          { label: 'Patient List',  key: 'patient-list' },
          { label: 'Schedule',      key: 'schedule' },
        ].map(tab => (
          <Box key={tab.key} onClick={() => tab.key !== 'ed' && selectTab(tab.key as 'patient-list' | 'schedule')} sx={{ px: 2, py: 0.75, cursor: 'pointer', fontSize: '0.8rem', fontWeight: activeTab === tab.key && !appOpen ? 600 : 400, color: activeTab === tab.key && !appOpen ? C.text : '#aaa', bgcolor: activeTab === tab.key && !appOpen ? C.content : 'transparent', borderRadius: '4px 4px 0 0', '&:hover': { color: '#fff' } }}>
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      {appOpen ? (
        /* SMART Application: embed the corresponding MedArrive page in place of
           the EHR body (beneath the tab strip). */
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <iframe
            src={smartSrc}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            title={`MedArrive ${smartLabel}`}
          />
        </Box>
      ) : (
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left sidebar */}
        <Box sx={{ width: 198, bgcolor: C.sidebar, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0 }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>Patient Lists</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: C.blue, pl: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>+ Edit List…</Typography>
          </Box>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, mb: 0.5 }}>My Lists</Typography>
            {(['my-patients', 'my-unit'] as const).map(key => (
              <Box key={key} onClick={() => { setActiveList(key); selectTab('patient-list'); }} sx={{ pl: 1.5, py: 0.3, cursor: 'pointer', borderRadius: 0.5, bgcolor: activeList === key && activeTab === 'patient-list' ? '#dbeafe' : 'transparent', '&:hover': { bgcolor: '#f0f4ff' } }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: activeList === key && activeTab === 'patient-list' ? 600 : 400, color: activeList === key && activeTab === 'patient-list' ? C.blue : C.text }}>
                  ⊙ {key === 'my-patients' ? 'My Patients' : 'My Unit'}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, mb: 0.5 }}>Available Lists</Typography>
            {['Recent Searches', 'IP System Lists', 'IP RMC – Riverside Medical Center'].map(l => (
              <Typography key={l} sx={{ fontSize: '0.75rem', color: C.muted, pl: 1.5, py: 0.2 }}>{l}</Typography>
            ))}
          </Box>
        </Box>

        {/* Main + right panel */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Content header bar */}
            <Box sx={{ px: 2, py: 0.9, bgcolor: C.sidebar, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.8rem', color: C.muted }}>
                {activeTab === 'schedule' ? 'Schedule' : `Patient List — ${activeList === 'my-unit' ? 'My Unit' : 'My Patients'}`}
              </Typography>
              {activeTab === 'patient-list' && (
                <Box sx={{ px: 1.5, py: 0.3, bgcolor: C.blue, borderRadius: 4, cursor: 'pointer' }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>+ New Patient</Typography>
                </Box>
              )}
            </Box>
            {/* View */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {activeTab === 'patient-list'
                ? <MyUnitView onSelect={setSelected} />
                : <ScheduleGridView />
              }
            </Box>
            {/* Bottom patient summary */}
            <PatientSummaryPanel patient={selected} />
          </Box>
          {/* Right panel: only on Schedule tab */}
          {activeTab === 'schedule' && <VerticalSchedulePanel />}
        </Box>
      </Box>
      )}
    </Box>
  );
}
